import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_partners: number
  active_partners: number
  developers_registered: number
  api_keys_issued: number
  partner_revenue_mtd: number
  avg_satisfaction_score: number
}

interface Partner {
  id: string
  name: string
  tier: string
  developers: number
  api_calls_mtd: number
  revenue_mtd: number
}

interface TierDistribution {
  tier: string
  partners: number
  revenue_share: number
}

export default function PartnerPortalPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [partners, setPartners] = useState<Partner[]>([])
  const [tiers, setTiers] = useState<TierDistribution[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, partnersRes, tiersRes] = await Promise.all([
          fetch('/api/partnerportal/statistics'),
          fetch('/api/partnerportal/partners'),
          fetch('/api/partnerportal/tiers')
        ])
        setStatistics(await statsRes.json())
        setPartners(await partnersRes.json())
        setTiers(await tiersRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getTierBadge = (tier: string) => {
    const colors: Record<string, string> = {
      'Platinum': 'bg-purple-100 text-purple-800',
      'Gold': 'bg-yellow-100 text-yellow-800',
      'Silver': 'bg-gray-100 text-gray-800',
      'Bronze': 'bg-orange-100 text-orange-800'
    }
    return colors[tier] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Partner Portal...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Partner Portal</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_partners}</div>
              <div className="text-sm text-gray-500">Total Partners</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_partners}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">{statistics.developers_registered}</div>
              <div className="text-sm text-gray-500">Developers</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.api_keys_issued.toLocaleString()}</div>
              <div className="text-sm text-gray-500">API Keys</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">${(statistics.partner_revenue_mtd / 1000).toFixed(0)}K</div>
              <div className="text-sm text-gray-500">Revenue MTD</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.avg_satisfaction_score}/5</div>
              <div className="text-sm text-gray-500">Satisfaction</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Partners</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Partner</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tier</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Developers</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">API Calls MTD</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Revenue MTD</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {partners.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getTierBadge(p.tier)}`}>
                          {p.tier}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{p.developers}</td>
                      <td className="px-4 py-3 text-gray-600">{(p.api_calls_mtd / 1000000).toFixed(1)}M</td>
                      <td className="px-4 py-3 text-gray-600">${p.revenue_mtd.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Tier Distribution</h2></div>
            <div className="p-4 space-y-3">
              {tiers.map((tier, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 rounded-full text-xs ${getTierBadge(tier.tier)}`}>
                      {tier.tier}
                    </span>
                    <span className="text-sm font-medium">{tier.partners} partners</span>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-sm text-gray-500 mb-1">
                      <span>Revenue Share</span>
                      <span>{tier.revenue_share}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{width: `${tier.revenue_share}%`}}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
